import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { message, Form } from "antd";

import PreLiquidationService from "../../../../../../../shared/services/collection/preliquidation";

import { StepOneFormContext } from "../../../StepOne/contexts/StepOneForm";
import { StepThreeFormContext } from "./StepThreeFormContext";
import { GenericDataFormContext } from "../../../../shared/contexts/GenericDataForm";

import { Combo } from "../../../../../../../shared/interfaces";
import {
  PaymentMethodDataForServices,
  PaymentMethodFormValuesInput,
  PaymentMethodsAdded,
  StepThreePaymentMethodForm,
} from "../../interfaces";
import { LOADING_SAVED_PRELIQUIDATIONS_STATES, PreLiquidationStep } from "../../../../shared/interfaces";

import { ActionButton } from "@hdi-seguros/web-components.action-button";
import { DeleteOutlined, PaperClipOutlined } from "@ant-design/icons";
import { useFileHandler } from "../../hooks/useFileHandler";
import {
  FileToUpload,
  PaymentListValues,
} from "../../../../shared/interfaces/services/get-payments-list-service.interface";
import { DocumentUploadServiceRequestParams } from "../../../../../../../shared/interfaces/services/post-send-claim-documents-service";
import dayjs from "dayjs";
import { AxiosResponse } from "axios";
import { PAYMENT_METHODS } from "../../../../shared/utils/methods-payment.enum";
import {
  hasPaymentsMissingRequiredFile,
  PAYMENT_FILE_REQUIRED_MESSAGE,
} from "../../../../shared/utils/payment-file-validation";
import { GET_BANKS_LIST_SERVICE_MOCK } from "../../../../shared/utils";
import MasterService from "../../../../../../../shared/services/master";

const {
  STEP_ONE_DATA_FILLED,
  STEP_THREE_SERVICES_LOADING,
  STEP_THREE_SERVICES_LOADED,
  HAS_ERROR,
} = LOADING_SAVED_PRELIQUIDATIONS_STATES;

export const StepThreeFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    preliquidationInfo,
    setCanShowSpinner,
    loadPreliquidationState,
    setLoadPreliquidationState,
    loadingSavedPreliquidation,
    setLoadingSavedPreliquidation,
    currentStep,
  } = useContext(GenericDataFormContext);
  const { totalSelected } = useContext(StepOneFormContext);
  const [paymentMethods, setPaymentMethods] = useState<Array<Combo>>();
  const [banksOptions, setBanksOptions] = useState<Array<Combo>>();
  const [allBanksOptions, setAllBanksOptions] = useState<Array<Combo>>();
  const [paymentsList, setPaymentsList] = useState<Array<PaymentListValues>>(
    []
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState<Array<FileToUpload>>([]);

  const [paymentMethodSequence, setPaymentMethodSequence] = useState<number>(1);
  const [depositBankNumber, setDepositBankNumber] = useState<number>(1);
  const [
    depositIntegratedCollectionNumber,
    setDepositIntegratedCollectionNumber,
  ] = useState<number>(1);

  const [stepThreePaymentMethodForm] =
    Form.useForm<StepThreePaymentMethodForm>();

  const [paymentMethodFormValues, setPaymentMethodFormValues] = useState<
    PaymentMethodFormValuesInput | undefined
  >();
  const [paymentMethodsDataTable, setPaymentMethodsDataTable] = useState<
    Array<PaymentMethodsAdded>
  >([]);

  const [paymentsToLoad, setPaymentsToLoad] = useState<any[]>([]);
  const [filesToSend, setFilesToSend] = useState<
    DocumentUploadServiceRequestParams[]
  >([]);

  const { cutText } = useFileHandler(stepThreePaymentMethodForm);

  const capitalizeFirstLetter = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const formatLabelWithSpace = (str: string) => str.replace(/\(/g, " (");

  const calculatedTotals = useMemo(() => {
    const totalAmountPaid = paymentMethodsDataTable.reduce(
      (sum, method) => sum + Number(method.exactAmount || 0),
      0
    );

    const totalRemainder = Number((totalSelected - totalAmountPaid).toFixed(2));

    return { totalAmountPaid, totalRemainder };
  }, [paymentMethodsDataTable, totalSelected]);

  const getBankLabel = (codigoBanco: number, paymentMethod: number) => {
    if (paymentMethod === PAYMENT_METHODS.ELECTRONIC_CHECK) {
      return allBanksOptions?.find((e) => String(codigoBanco) === e.value)
        ?.label;
    }
    return banksOptions?.find((e) => String(codigoBanco) === e.value)?.label;
  };

  const getPaymentList = /* useCallback(*/
    async (currentPayments?: any) => {
      const response = await PreLiquidationService.getPaymentsList(
        preliquidationInfo?.numeroPreliquidacion!
      );
      let transformPaymentsLoaded = [];
      if (currentPayments) {
        transformPaymentsLoaded = currentPayments.map((item: any) => ({
          key: String(Math.random()),
          paymentMethod: paymentMethods?.find(
            (e) => String(item.newPaymentMethod) === e.value
          )?.label,
          exactAmount: item.basePaymentMethodData.monto,
          bank: getBankLabel(
            item.basePaymentMethodData.codigoBanco,
            item.newPaymentMethod
          ),
          checkNumber: item.basePaymentMethodData.numeroCheque,
          codePaymentMethod: item.newPaymentMethod,
          Date:
            item.newPaymentMethod !== 5
              ? item.basePaymentMethodData.fechaDeposito
              : item.basePaymentMethodData.fecha,
          proofOfPayment: item.basePaymentMethodData?.archivo && item.basePaymentMethodData.archivo !== "0" && item.basePaymentMethodData.archivo !== "false" && item.basePaymentMethodData.archivo !== "" ? item.basePaymentMethodData.archivo : null,
          isSaved: false,
        }));
      }

      setPaymentsList(response.data.Valores);      
      if(paymentMethodSequence <= response.data.Valores.length){
        setPaymentMethodSequence(response.data.Valores.length + 1)
        setDepositBankNumber(response.data.Valores.length + 1)
      }
      
      let transformedData = response.data.Valores.map((item) => ({
        key: String(Math.random()),
        paymentMethod: item.formaDePago,
        exactAmount: item.importe,
        bank: item.banco,
        Date: item.fecha,
        checkNumber: item.nroCheque,
        codePaymentMethod: item.codigoFormaDePago,
        proofOfPayment: item.archivo && item.archivo !== "0" && item.archivo !== "false" && item.archivo !== "" ? item.archivo : null,
        isSaved: true,
      }));

      transformedData = [...transformedData, ...transformPaymentsLoaded];

      const paymentMethodsDatasource = transformedData.map((payment: any, index) => {        
        const newPaymentMethod: PaymentMethodsAdded = {
          key: payment.key,
          codePaymentMethod: payment.codePaymentMethod,
          paymentMethod: payment.paymentMethod,
          exactAmount: payment.exactAmount,
          bank: payment.bank,
          transferDate: payment.codePaymentMethod !== 5 ? payment.Date : null,
          checkDate: payment.codePaymentMethod === 5 ? payment.Date : null,
          checkNumber: payment.checkNumber,
          observations: payment.observations ?? "-",
          proofOfPayment: payment.proofOfPayment ? (
            //** Note preliquidation: Podríamos agregar el abrir archivo pero depende de la implementación **/
            <ActionButton
              label={cutText(payment.proofOfPayment, 20)}
              icon={<PaperClipOutlined />}
              color="danger"
              type="text"
            />
          ) : (
            "-"
          ),
          action: (
            <ActionButton
              label=""
              icon={<DeleteOutlined />}
              color="danger"
              type="secondary"
              onClick={() =>
                onDeletePaymentMethod({
                  key: payment.key,
                  codePaymentMethod: payment.codePaymentMethod,
                  numberOfBankSelected: payment.bank,
                  transferDate:
                    payment.paymentMethod !== "" ? payment.Date : "-",
                  checkDate: payment.paymentMethod !== "" ? payment.Date : "-",
                  exactAmount: payment.exactAmount,
                  checkNumber: payment.checkNumber,
                  observations: payment.observations ?? "-",
                  proofOfPayment: payment.proofOfPayment,
                  paymentMethodSequence: index,
                  depositNumber: payment.checkNumber,
                  isSaved: payment.isSaved,
                  index: transformedData.findIndex(
                    (e) => e.key === payment.key
                  ),
                })
              }
            />
          ),
        };

        return newPaymentMethod;
      });

      setPaymentMethodsDataTable(paymentMethodsDatasource);
    }/*,
    [
      preliquidationInfo,
      setPaymentMethodsDataTable,
      preliquidationInfo,
      setLoadPreliquidationState,
      loadPreliquidationState,
      loadingSavedPreliquidation,
      banksOptions,
      filesToUpload,
      paymentsToLoad,
    ]
  );*/

  const callStepThreeServices = useCallback(async () => {
    try {
      setCanShowSpinner(true);

      setLoadPreliquidationState(STEP_THREE_SERVICES_LOADING);

      await getPaymentList();

      setLoadPreliquidationState(STEP_THREE_SERVICES_LOADED);
    } catch (error) {
      setLoadPreliquidationState(HAS_ERROR);
      setLoadingSavedPreliquidation(false);
    } finally {
      setCanShowSpinner(false);
    }
  }, [
    setLoadPreliquidationState,
    getPaymentList,
    setCanShowSpinner,
    setLoadingSavedPreliquidation,
  ]);

  const getPaymentMethodsOptions = useCallback(async () => {
    try {
      setCanShowSpinner(true);

      const response =
        await PreLiquidationService.getPreliquidationPaymentMethodsList();

      if (response) {
        const transformedData = response.data.formasDePagoPreliqu.map(
          (item) => ({
            label: capitalizeFirstLetter(item.descripcion),
            value: item.codigo.toString(),
          })
        );

        setPaymentMethods(transformedData);
      }
    } catch (error) {
    } finally {
      setCanShowSpinner(false);
    }
  }, [setCanShowSpinner, setPaymentMethods]);

  const getBanksOptions = useCallback(async () => {
    try {
      setCanShowSpinner(true);

      const response = await PreLiquidationService.getBanksList();

      if (response) {
        const transformedData = response.data.bancosPreliquidacion.map(
          (item) => ({
            label: formatLabelWithSpace(item.descripcion),
            value: item.codigo.toString(),
          })
        );

        setBanksOptions(transformedData);
      }
    } catch (error) {
    } finally {
      setCanShowSpinner(false);
    }
  }, [setCanShowSpinner, setBanksOptions]);

  const getAllBanksOptions = useCallback(async (): Promise<Array<Combo> | undefined> => {
    try {
      setCanShowSpinner(true);

      const response = await MasterService.getAllBanks();

      if (response) {
        const transformedData = response.data.bancos.map((item) => ({
          label: formatLabelWithSpace(item.NombreBanco),
          value: item.CodBancoCheke.toString(),
        }));

        setAllBanksOptions(transformedData);
        return transformedData;
      }
      return undefined;
    } catch (error) {
      return undefined;
    } finally {
      setCanShowSpinner(false);
    }
  }, [setCanShowSpinner, setAllBanksOptions]);

  const postPaymentMethod = useCallback(
    async (
      codePaymentMethod: number,
      params: PaymentMethodDataForServices
    ): Promise<AxiosResponse<any>> => {
      const preliquidationNumber = preliquidationInfo?.numeroPreliquidacion!;

      try {
        switch (codePaymentMethod) {
          case 4:
            return await PreLiquidationService.insertBankDeposit({
              ...params,
              numeroPreliquidacion: preliquidationNumber,
            });

          case 8:
            return await PreLiquidationService.insertIntegratedCollection({
              ...params,
              numeroPreliquidacion: preliquidationNumber,
            });

          case 5:
            return await PreLiquidationService.insertElectronicCheck({
              ...params,
              numeroPreliquidacion: preliquidationNumber,
            });

          case 6:
            return await PreLiquidationService.insertRetention({
              ...params,
              numeroPreliquidacion: preliquidationNumber,
            });

          case 3:
            return await PreLiquidationService.insertRounding({
              ...params,
              numeroPreliquidacion: preliquidationNumber,
            });

          default:
            throw new Error(
              `Método de pago no soportado: ${codePaymentMethod}`
            );
        }
      } catch (error) {
        throw error;
      }
    },
    [preliquidationInfo]
  );

  const processPaymentMethod = async (
    newPaymentMethod: number,
    basePaymentMethodData: PaymentMethodDataForServices,
    depositBankNumber: number,
    depositIntegratedCollectionNumber: number
  ): Promise<AxiosResponse<any>> => {
    if (newPaymentMethod === PAYMENT_METHODS.BANK_DEPOSIT) {
      return postPaymentMethod(newPaymentMethod, {
        ...basePaymentMethodData,
        numeroDeposito: depositBankNumber.toString(),
      });
    }

    if (newPaymentMethod === PAYMENT_METHODS.GALICIA_INTEGRATED_COLLECTION) {
      return postPaymentMethod(newPaymentMethod, {
        ...basePaymentMethodData,
        numeroDeposito: depositIntegratedCollectionNumber.toString(),
      });
    }

    return postPaymentMethod(newPaymentMethod, basePaymentMethodData);
  };

  const deletePaymentMethod = useCallback(
    async (codePaymentMethod: number, params: PaymentMethodDataForServices) => {
      const preliquidationNumber = preliquidationInfo?.numeroPreliquidacion!;
      const paymentTypeCode = Number(codePaymentMethod.toString().slice(1, 2));

      switch (paymentTypeCode) {
        case 4:
          await PreLiquidationService.deleteBankDeposit({
            ...params,
            numeroPreliquidacion: preliquidationNumber,
          });
          break;

        case 8:
          await PreLiquidationService.deleteIntegratedCollection({
            ...params,
            numeroPreliquidacion: preliquidationNumber,
          });
          break;

        case 5:
          await PreLiquidationService.deleteElectronicCheck({
            ...params,
            numeroPreliquidacion: preliquidationNumber,
          });
          break;

        case 6:
          await PreLiquidationService.deleteRetention({
            ...params,
            numeroPreliquidacion: preliquidationNumber,
          });
          break;

        case 3:
          await PreLiquidationService.deleteRounding({
            ...params,
            numeroPreliquidacion: preliquidationNumber,
          });
          break;

        default:
          break;
      }
    },
    [preliquidationInfo]
  );

  //TODO mover a useDeletePaymentHandler.tsx
  const onDeletePaymentMethod = async ({
    key,
    codePaymentMethod,
    numberOfBankSelected,
    transferDate,
    checkDate,
    exactAmount,
    checkNumber,
    observations,
    proofOfPayment,
    paymentMethodSequence,
    depositNumber,
    isSaved,
    index,
  }: {
    key: string;
    codePaymentMethod: number;
    numberOfBankSelected: string;
    transferDate: string;
    checkDate: string;
    exactAmount: string;
    checkNumber: string;
    observations: string;
    proofOfPayment: string;
    paymentMethodSequence: number;
    depositNumber: number;
    isSaved: boolean;
    index: number;
  }) => {
    if (checkNumber) checkNumber = checkNumber.toString();

    exactAmount = exactAmount.toString();
    proofOfPayment = proofOfPayment ? proofOfPayment.toString() : "0";
    transferDate = transferDate ? dayjs(transferDate).format("YYYYMMDD") : "-";
    checkDate = checkDate ? dayjs(checkDate).format("YYYYMMDD") : "-";
    
    let bankCode;
    if(codePaymentMethod.toString() === "05"){
      const allBanksData = await getAllBanksOptions();
      
      bankCode = allBanksData?.find(
        (el: any) => el.label === numberOfBankSelected
      )?.value ?? "";      
    }else{
      bankCode = GET_BANKS_LIST_SERVICE_MOCK.bancosPreliquidacion.find(
        (bank) => bank.descripcion === numberOfBankSelected
      )?.codigo;
    }

    try {
      setCanShowSpinner(true);

      if (isSaved) {
        await deletePaymentMethod(codePaymentMethod, {
          codigoBanco: bankCode?.toString() ?? "",
          fechaDeposito: transferDate,
          fecha: checkDate,
          monto: exactAmount,
          numeroCheque: checkNumber,
          observaciones: observations,
          archivo: proofOfPayment,
          secuencia: paymentMethodSequence.toString(),
          numeroDeposito: depositNumber.toString(),
        });
        setFilesToSend([]);
        setPaymentsToLoad([]);
        await getPaymentList();
      } else {
        // Encontrar el índice en paymentsToLoad antes de eliminarlo
        const paymentIndexInPaymentsToLoad = paymentsToLoad.findIndex((payment: any) => {
          const matchesSecuencia = payment.basePaymentMethodData?.secuencia === key;
          const matchesCodeAndAmount = payment.newPaymentMethod === codePaymentMethod && 
                                       String(payment.basePaymentMethodData?.monto) === String(exactAmount);
          return matchesSecuencia || matchesCodeAndAmount;
        });
        
        // Eliminar de la tabla
        setPaymentMethodsDataTable((prev) => prev.filter((item: PaymentMethodsAdded) => item.key !== key));
        
        // Para pagos no guardados, buscar por secuencia (que es el key) o por código+monto
        setPaymentsToLoad((prev) => {
          const filtered = prev.filter((payment: any) => {
            // Buscar por secuencia (key) o por código de método de pago y monto
            const matchesSecuencia = payment.basePaymentMethodData?.secuencia === key;
            const matchesCodeAndAmount = payment.newPaymentMethod === codePaymentMethod && 
                                         String(payment.basePaymentMethodData?.monto) === String(exactAmount);
            return !(matchesSecuencia || matchesCodeAndAmount);
          });
          return filtered;
        });
        
        // Para filesToSend, buscar por el nombre del archivo o por índice
        setFilesToSend((prev) => {
          // Si el proofOfPayment es un string válido, buscar por ese nombre
          const proofOfPaymentStr = typeof proofOfPayment === 'string' ? proofOfPayment : '';
          if (proofOfPaymentStr && proofOfPaymentStr !== "0" && proofOfPaymentStr !== "false" && proofOfPaymentStr !== "") {
            return prev.filter((file: any) => {
              const fileName = file.uploadDocument?.fileName;
              return fileName !== proofOfPaymentStr;
            });
          }
          // Usar el índice encontrado en paymentsToLoad si está disponible
          if (paymentIndexInPaymentsToLoad >= 0 && paymentIndexInPaymentsToLoad < prev.length) {
            return prev.filter((_, i) => i !== paymentIndexInPaymentsToLoad);
          }
          // Si no se encuentra, usar el índice pasado como parámetro
          return prev.filter((_, i) => i !== index);
        });
      }
    } catch (error) {
    } finally {
      setCanShowSpinner(false);
    }
  };

  const postSavePreliquidation = useCallback(async () => {
    try {
      setCanShowSpinner(true);
      const numeroPreliquidacion = preliquidationInfo?.numeroPreliquidacion!;

      await PreLiquidationService.savePreliquidation(numeroPreliquidacion);
    } catch (error) {
    } finally {
      setCanShowSpinner(false);
    }
  }, [setCanShowSpinner, preliquidationInfo]);

  const proccessPayments = useCallback(async (): Promise<boolean> => {
    setCanShowSpinner(true);
    try {
      const sortedPayments = [
        ...paymentsToLoad.filter((p) => p.newPaymentMethod === 3),
        ...paymentsToLoad.filter((p) => p.newPaymentMethod !== 3),
      ];

      for (let i = 0; i < sortedPayments.length; i++) {
        const payment = sortedPayments[i];
        const fileTransformed = payment.fileToSend;

        try {
          const response = await processPaymentMethod(
            payment.newPaymentMethod,
            payment.basePaymentMethodData,
            payment.depositBankNumber,
            payment.depositIntegratedCollectionNumber
          );

          if (!response) {
            message.error(
              "No se pudo registrar una de las formas de pago. Verifique los datos e intente nuevamente."
            );
            return false;
          }
          if (fileTransformed) {
            const uploadStatus = await postUploadDocumentation(fileTransformed);
            if (uploadStatus < 200 || uploadStatus >= 300) {
              message.error(
                `No se pudo subir el comprobante "${fileTransformed.uploadDocument.fileName}". Intente cargarlo nuevamente.`
              );
              return false;
            }
          }
        } catch (err) {
          message.error(
            "Ocurrió un error al procesar las formas de pago. Intente nuevamente."
          );
          return false;
        }
      }

      return true;
    } finally {
      setCanShowSpinner(false);
    }
  }, [paymentsToLoad]);

  const postSendPreliquidation = useCallback(async () => {
    try {
      setCanShowSpinner(true);
      const numeroPreliquidacion = preliquidationInfo?.numeroPreliquidacion!;

      if (hasPaymentsMissingRequiredFile(paymentsToLoad, paymentsList)) {
        message.error(PAYMENT_FILE_REQUIRED_MESSAGE);
        throw new Error(PAYMENT_FILE_REQUIRED_MESSAGE);
      }
      
      const response = await PreLiquidationService.sendPreliquidation(
        numeroPreliquidacion
      );
      return response;
    } catch (error) {
      throw error;
    } finally {
      setCanShowSpinner(false);
    }
  }, [setCanShowSpinner, preliquidationInfo, paymentsList, paymentsToLoad]);

  const postUploadDocumentation = useCallback(
    async (params: DocumentUploadServiceRequestParams): Promise<number> => {
      try {
        const response =
          await PreLiquidationService.postUploadPreliquidationDocumentation({
            ...params,
          });
        return response.status;
      } catch (error) {
        return 500;
      }
    },
    []
  );

  useEffect(() => {
    if (loadPreliquidationState === STEP_ONE_DATA_FILLED) {
      callStepThreeServices();
    }
  }, [loadPreliquidationState]);

  
  useEffect(() => {
    if (
      currentStep === PreLiquidationStep.THREE &&
      paymentMethodsDataTable.length === 0 &&
      preliquidationInfo?.numeroPreliquidacion &&
      !loadingSavedPreliquidation
    ) {
      const timer = setTimeout(() => {
        getPaymentList(paymentsToLoad.length > 0 ? paymentsToLoad : undefined);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentStep, paymentMethodsDataTable.length, preliquidationInfo?.numeroPreliquidacion, loadingSavedPreliquidation, paymentsToLoad]);

  const values = useMemo(
    () => ({
      stepThreePaymentMethodForm,
      paymentMethodFormValues,
      setPaymentMethodFormValues,
      paymentMethodsDataTable,
      setPaymentMethodsDataTable,
      getPaymentMethodsOptions,
      getBanksOptions,
      paymentMethods,
      banksOptions,
      postPaymentMethod,
      deletePaymentMethod,
      postSavePreliquidation,
      postSendPreliquidation,
      postUploadDocumentation,
      paymentMethodSequence,
      setPaymentMethodSequence,
      depositBankNumber,
      setDepositBankNumber,
      depositIntegratedCollectionNumber,
      setDepositIntegratedCollectionNumber,
      calculatedTotals,
      getPaymentList,
      paymentsList,
      setPaymentsList,
      filesToUpload,
      setFilesToUpload,
      filesToSend,
      setFilesToSend,
      paymentsToLoad,
      setPaymentsToLoad,
      proccessPayments,
      getAllBanksOptions,
      allBanksOptions,
      setHasChanges,
      hasChanges,
      getBankLabel
    }),
    [
      stepThreePaymentMethodForm,
      paymentMethodFormValues,
      setPaymentMethodFormValues,
      paymentMethodsDataTable,
      setPaymentMethodsDataTable,
      getPaymentMethodsOptions,
      getBanksOptions,
      paymentMethods,
      banksOptions,
      postPaymentMethod,
      deletePaymentMethod,
      postSavePreliquidation,
      postSendPreliquidation,
      postUploadDocumentation,
      paymentMethodSequence,
      setPaymentMethodSequence,
      depositBankNumber,
      setDepositBankNumber,
      depositIntegratedCollectionNumber,
      setDepositIntegratedCollectionNumber,
      calculatedTotals,
      getPaymentList,
      paymentsList,
      setPaymentsList,
      filesToUpload,
      setFilesToUpload,
      filesToSend,
      setFilesToSend,
      paymentsToLoad,
      setPaymentsToLoad,
      proccessPayments,
      getAllBanksOptions,
      allBanksOptions,
      setHasChanges,
      hasChanges
    ]
  );

  return (
    <StepThreeFormContext.Provider value={values}>
      {children}
    </StepThreeFormContext.Provider>
  );
};